// Locale state: module-level + localStorage, mirroring lib/theme.ts so React
// and non-React code (format.ts date helpers) share one source of truth.
// Kept separate from i18n.tsx so format.ts can read the locale without an
// import cycle (i18n.tsx imports labelize from format.ts).

export const LOCALES = ["en", "de", "fr"] as const;
export type Locale = (typeof LOCALES)[number];

const STORAGE_KEY = "gaea-locale";

function isLocale(value: unknown): value is Locale {
  return typeof value === "string" && (LOCALES as readonly string[]).includes(value);
}

/** navigator.language ("de-CH") matched against supported locales, else en. */
function detect(): Locale {
  if (typeof navigator !== "undefined") {
    const lang = navigator.language?.slice(0, 2).toLowerCase();
    if (isLocale(lang)) return lang;
  }
  return "en";
}

function readStored(): Locale {
  try {
    const raw = typeof localStorage !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
    return isLocale(raw) ? raw : detect();
  } catch {
    return detect();
  }
}

let locale: Locale = readStored();
const listeners = new Set<() => void>();

export function getLocale(): Locale {
  return locale;
}

export function setLocale(next: Locale): void {
  if (next === locale) return;
  locale = next;
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    /* persistence is best-effort */
  }
  for (const l of listeners) l();
}

export function subscribeLocale(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

// Keep multiple tabs in step, same as lib/theme.ts.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key === STORAGE_KEY || e.key === null) {
      const next = readStored();
      if (next !== locale) {
        locale = next;
        for (const l of listeners) l();
      }
    }
  });
}
